import React, { useState, useEffect, createContext, useContext } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  collection,
  getDocs,
} from "firebase/firestore";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { db, auth } from "./firebase";
import Map from "./Map";
import ProjectDetails from "./ProjectDetails";
import LoginPage from "./LoginPage";
import "./App.css";
import Logo from "./assets/logo.png";

/* --------------------------------------------------------------
   1. Auth Context – 1-day session with sessionStorage
   -------------------------------------------------------------- */
const AuthContext = createContext();
const useAuth = () => useContext(AuthContext);

const SESSION_KEY = "hasiru_session";
const ONE_DAY_MS = 24 * 60 * 60 * 1000; // 24 hours

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const storeSession = async (firebaseUser) => {
    const token = await firebaseUser.getIdToken();
    const payload = {
      token,
      uid: firebaseUser.uid,
      expiresAt: Date.now() + ONE_DAY_MS,
    };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(payload));
  };

  const clearSession = () => {
    sessionStorage.removeItem(SESSION_KEY);
  };

  const login = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    await storeSession(cred.user);
    return cred.user;
  };

  const logout = async () => {
    await signOut(auth);
    clearSession();
  };

  useEffect(() => {
    const checkStoredSession = () => {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) {
        setAuthLoading(false);
        return;
      }

      let stored;
      try {
        stored = JSON.parse(raw);
      } catch {
        stored = null;
      }

      if (!stored || Date.now() > stored.expiresAt) {
        clearSession();
        setAuthLoading(false);
        return;
      }

      setAuthLoading(false);
    };

    checkStoredSession();

    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      if (u) {
        await storeSession(u);
        setUser(u);
      } else {
        clearSession();
        setUser(null);
      }
      setAuthLoading(false);
    });

    return unsubscribe;
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, authLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

/* --------------------------------------------------------------
   2. Protected Route
   -------------------------------------------------------------- */
function RequireAuth({ children }) {
  const { user, authLoading } = useAuth();
  const location = useLocation();

  if (authLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-container">
          <img src={Logo} alt="Logo" className="loading-logo" />
          <div className="loader"></div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

/* --------------------------------------------------------------
   3. Logout Button
   -------------------------------------------------------------- */
function LogoutButton() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <button onClick={handleLogout} className="logout-btn">
      Logout
    </button>
  );
}

/* --------------------------------------------------------------
   4. Main App
   -------------------------------------------------------------- */
function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [plots, setPlots] = useState([]);

  // Fetch plots
  useEffect(() => {
    const fetchPlots = async () => {
      try {
        const snap = await getDocs(collection(db, "plots"));
        const data = snap.docs.map((d) => ({ projectId: d.id, ...d.data() }));
        setPlots(data);
      } catch (e) {
        console.error(e);
        setPlots([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPlots();

    const timeout = setTimeout(() => setIsLoading(false), 3000);
    return () => clearTimeout(timeout);
  }, []);

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-container">
          <img src={Logo} alt="Logo" className="loading-logo" />
          <div className="loader"></div>
        </div>
      </div>
    );
  }

  return (
    <AuthProvider>
      <div className="app-container">
        <Router>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<LoginPage />} />

            {/* Protected */}
            <Route
              path="/"
              element={
                <RequireAuth>
                  <Map plots={plots} logout={<LogoutButton />} />
                </RequireAuth>
              }
            />
            <Route
              path="/map"
              element={
                <RequireAuth>
                  <Map plots={plots} logout={<LogoutButton />} />
                </RequireAuth>
              }
            />
            <Route
              path="/project/:projectId"
              element={
                <RequireAuth>
                  <ProjectDetails logout={<LogoutButton />} />
                </RequireAuth>
              }
            />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/map" replace />} />
          </Routes>
        </Router>
      </div>
    </AuthProvider>
  );
}

export default App;

export { useAuth };